import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatMelbourneDate } from '@/lib/melbourneTime';
import { openInNewTab } from '@/lib/openInNewTab';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Plus, 
  Calendar, 
  Car, 
  User, 
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
  ArrowRightCircle,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import QuickBookingModal from '@/components/QuickBookingModal';
import FilterPresetManager from '@/components/FilterPresetManager';
import CapacityBanner from '@/components/job-cards/CapacityBanner';
import OnLeaveBanner from '@/components/OnLeaveBanner';
import BulkBookingSmsModal from '@/components/BulkBookingSmsModal';
import { useCommsGate } from '@/components/CommsUpgradeDialog';
import RelatedItemsPanel from '@/components/RelatedItemsPanel';
import JobCardSearchAutoComplete from '@/components/job-cards/JobCardSearchAutoComplete';
import MechanicWalletBoard from '@/components/job-cards/MechanicWalletBoard';
import StarPerformerBanner from '@/components/job-cards/StarPerformerBanner';
import { 
  JobCardDetailModal, 
  JobCardItem, 
  STATUS_CONFIG, 
  PRIORITY_CONFIG 
} from '@/components/job-cards';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VIEW_TYPES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom Range' }
];

const SERVICE_TYPE_FILTERS = [
  { value: 'all', label: 'All Services' },
  { value: 'service', label: 'General Service' },
  { value: 'rwc', label: 'RWC (Roadworthy)' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'aircon', label: 'Aircon' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' }
];

/**
 * XHR-based PUT/POST helper that bypasses the global `window.fetch`
 * interceptor injected by `emergent-main.js` (debug monitor) which
 * consumes the response body on 4xx, leaving us unable to read the
 * structured `detail` payload. Use this whenever you need to inspect
 * a 4xx body (e.g. 409 MECHANIC_HAS_ACTIVE_JC). Returns
 * `{ status, body }` where `body` is the parsed JSON (or null).
 */
const xhrJson = (method, url, payload) => new Promise((resolve) => {
  try {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    try {
      const tok = localStorage.getItem('session_token');
      if (tok) xhr.setRequestHeader('Authorization', `Bearer ${tok}`);
    } catch (_) { /* cookie auth still works */ }
    xhr.withCredentials = true;
    xhr.onload = () => {
      let body = null;
      try { body = JSON.parse(xhr.responseText); } catch (_) { /* not JSON */ }
      resolve({ status: xhr.status, body });
    };
    xhr.onerror = () => resolve({ status: 0, body: null });
    xhr.send(payload === undefined ? null : JSON.stringify(payload));
  } catch (_e) {
    resolve({ status: 0, body: null });
  }
});

export default function JobCards() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingMode = searchParams.get('pending_prev_weeks') === '1';
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Kanban state
  const [columns, setColumns] = useState([]);
  const [viewType, setViewType] = useState('daily');
  const [targetDate, setTargetDate] = useState(new Date());
  const [totalCards, setTotalCards] = useState(0);
  const [dateRange, setDateRange] = useState({});

  // Tracks JC ids seen on the last fetch — used to detect newly-assigned
  // JCs landing on the mechanic's board so we can toast them without
  // a manual refresh. Held in refs so they don't trigger re-renders /
  // re-create fetchKanban (which would cause infinite poll loops).
  const seenJobCardIdsRef = useRef(null);
  const firstFetchDoneRef = useRef(false);

  // Board mode: "status" = traditional kanban columns; "wallet" = group
  // by mechanic with status chips. Persisted to localStorage so the SM
  // doesn't have to re-pick it every visit.
  const [boardMode, setBoardMode] = useState(() => {
    try { return localStorage.getItem('jc_board_mode') || 'status'; } catch (_e) { return 'status'; }
  });
  useEffect(() => {
    try { localStorage.setItem('jc_board_mode', boardMode); } catch (_e) { /* quota */ }
  }, [boardMode]);
  
  // Filter state
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  // Kanban search + status filter — lets SA/SM scope the board to just
  // missed bookings (no_show) or just picked-up jobs, and find a customer
  // by rego / name / mobile suffix without leaving the page. URL param
  // `?search=X` initialises the box (e.g. when arriving from another page
  // or the global search bar).
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusScope, setStatusScope] = useState('all'); // all | no_show | booking | pickup | in_progress | completed
  // iter139.1 — Quote column visibility toggle. Hidden by default (OFF)
  // so quotes don't clutter the operational kanban. SM flips ON to
  // follow up on outstanding quotes; clicking a quote tile lets SM
  // schedule it (which promotes the quote to a real booking).
  const [showQuoteColumn, setShowQuoteColumn] = useState(() => {
    try { return localStorage.getItem('jc-show-quote-column') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('jc-show-quote-column', showQuoteColumn ? '1' : '0'); } catch { /* noop */ }
  }, [showQuoteColumn]);
  // Mechanic filter — when set, kanban shows ONLY JCs assigned to this
  // mechanic so SM can see at-a-glance what one tech is working on.
  const [mechanicFilter, setMechanicFilter] = useState('all');

  // iter141 — Bulk SMS selection set for the Status view's Booking column.
  // Stores job_card_id of selected booking tiles. Wallet view uses its own
  // state inside MechanicWalletBoard so the two boards can't accidentally
  // share a stale selection across mode toggles.
  const [bookingSelectedIds, setBookingSelectedIds] = useState(new Set());
  const [bulkSmsOpen, setBulkSmsOpen] = useState(false);
  const { guardComms, commsDialog } = useCommsGate();
  // iter147 — Bulk SMS also on Quote + No Show columns (user request:
  // "I need same on no show and quote section as well to target those
  // customers"). Separate selection sets so a checkbox in one column
  // doesn't accidentally SMS a customer from another status.
  const [quoteSelectedIds, setQuoteSelectedIds] = useState(new Set());
  const [noShowSelectedIds, setNoShowSelectedIds] = useState(new Set());
  // The column status (booking | quote | no_show) that fed the currently
  // open BulkSms modal — controls which selection set is passed as
  // recipients and which set is cleared after send.
  const [bulkSmsSource, setBulkSmsSource] = useState('booking');
  const selectedIdsFor = {
    booking: bookingSelectedIds,
    quote: quoteSelectedIds,
    no_show: noShowSelectedIds,
  };
  const setSelectedIdsFor = {
    booking: setBookingSelectedIds,
    quote: setQuoteSelectedIds,
    no_show: setNoShowSelectedIds,
  };

  // iter147 — Per-section search + sort. User request:
  //   "small search bar on top of each section, only specific to that
  //    section (not global)"; "arrow to sort by date, default descending
  //    so SM sees most recent on top".
  //
  // Shape: `{ [status]: { search: '', sortDir: 'desc' } }`. Stored in
  // localStorage so the SM's preference persists across reloads.
  const [sectionUi, setSectionUi] = useState(() => {
    try {
      const cached = localStorage.getItem('jc-section-ui');
      if (cached) return JSON.parse(cached);
    } catch { /* noop */ }
    return {};
  });
  useEffect(() => {
    try { localStorage.setItem('jc-section-ui', JSON.stringify(sectionUi)); } catch { /* noop */ }
  }, [sectionUi]);
  const sectionUiFor = (status) => sectionUi[status] || { search: '', sortDir: 'desc' };
  const setSectionSearch = (status, value) =>
    setSectionUi((prev) => ({ ...prev, [status]: { ...sectionUiFor(status), search: value } }));
  const toggleSectionSort = (status) =>
    setSectionUi((prev) => ({
      ...prev,
      [status]: { ...sectionUiFor(status), sortDir: sectionUiFor(status).sortDir === 'asc' ? 'desc' : 'asc' },
    }));

  // Helper — pick the "best" date field to sort by for a given column status.
  // Completed sorts by completed_at (so a JC finished TODAY jumps to top even
  // if created weeks ago). Booking/no_show/drop_off by scheduled_date. Quote
  // + SM review by updated_at (last activity). Fallbacks chain to created_at
  // so a missing timestamp never crashes the sort.
  const getSortDateForCard = (card, status) => {
    let raw;
    if (status === 'completed' || status === 'delivered') {
      raw = card.completed_at || card.updated_at || card.created_at;
    } else if (status === 'booking' || status === 'no_show' || status === 'drop_off' || status === 'pickup') {
      raw = card.scheduled_date || card.updated_at || card.created_at;
    } else if (status === 'sm_review') {
      raw = card.reviewed_at || card.updated_at || card.created_at;
    } else {
      raw = card.updated_at || card.created_at || card.kanban_anchor_date;
    }
    return raw ? new Date(raw).getTime() : 0;
  };

  // Filter + sort the cards for a given column based on that column's
  // section search + sort UI. Returns the transformed cards array.
  const displayCardsFor = (column) => {
    const ui = sectionUiFor(column.status);
    let cards = column.cards || [];
    const q = (ui.search || '').trim().toLowerCase();
    if (q) {
      cards = cards.filter((c) => {
        const hay = [
          c.customer_name,
          c.customer_phone,
          c.customer_mobile,
          c.customer_email,
          c.vehicle_rego,
          c.vehicle_make,
          c.vehicle_model,
          c.job_card_number,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = ui.sortDir === 'asc' ? 1 : -1;
    // Priority-rank is the PRIMARY sort so the SM/FM drag-to-reorder set in
    // the Wallet view reflects verbatim in every Status column (incl. the
    // mechanic's iPad). Ranked cards float to the top in ascending rank order
    // (#1 first); unranked cards fall back to the per-column date sort below,
    // honouring the date-arrow toggle. This keeps the mechanic's queue in the
    // exact order the SM prioritised instead of a date-only order.
    cards = [...cards].sort((a, b) => {
      const ra = Number.isFinite(a.priority_rank) ? a.priority_rank : null;
      const rb = Number.isFinite(b.priority_rank) ? b.priority_rank : null;
      if (ra !== null && rb !== null && ra !== rb) return ra - rb;
      if (ra !== null && rb === null) return -1;
      if (ra === null && rb !== null) return 1;
      return (getSortDateForCard(a, column.status) - getSortDateForCard(b, column.status)) * dir;
    });
    return cards;
  };

  // Related-items panel (invoices / bookings / customers / vehicles that
  // share the mobile / rego / name being searched). Only fetched when the
  // search box has content.
  const [relatedItems, setRelatedItems] = useState(null);
  
  // Create job card dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // iter253 — "Create Job Card" now launches the New Booking flow instead
  const [showQuickBooking, setShowQuickBooking] = useState(false);
  const [bookingsWithoutCards, setBookingsWithoutCards] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState('medium');
  const [selectedMechanic, setSelectedMechanic] = useState('');
  const [mechanics, setMechanics] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  
  // Job card detail modal
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Workshop labour rate — surfaced on the Mechanic Wallet board so the
  // SM can see the $ value of a clock-vs-allocated overrun on each tile.
  // Fetched once per session. Defaults to 120 if the endpoint fails.
  const [labourRate, setLabourRate] = useState(120);

  // "Mechanic already has an active JC" conflict dialog state.
  // Surfaced when the backend rejects an in_progress move with a 409 and
  // a structured detail.code === 'MECHANIC_HAS_ACTIVE_JC' payload.
  const [activeJcConflict, setActiveJcConflict] = useState(null);
  const [parkingInFlight, setParkingInFlight] = useState(false);

  // Check if user can manage (drag/drop, assign, etc.)
  const canManage = user && ['super_admin', 'admin', 'service_manager', 'floor_manager'].includes(user.role);
  const isSuperAdmin = user && user.role === 'super_admin';
  const isMechanic = user && ['mechanic', 'technician'].includes(user.role);
  // Mechanics can drag-drop their OWN assigned JCs across status columns
  // (Pickup → In Progress → Blocked / Waiting for Parts / SM Review). This
  // is how they "pick up" a new job. The backend enforces the
  // single-in-progress rule + RBAC on actual transitions.
  const canDragDrop = (canManage || isMechanic) && ['daily', 'weekly', 'custom'].includes(viewType);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const fetchKanban = useCallback(async () => {
    if (!user) return;
    
    try {
      let url;
      if (pendingMode && statusScope === 'deleted') {
        // iter126 hotfix — in pendingMode the SM picked "Deleted
        // (recoverable)" from the status filter. Pending-previous-weeks
        // doesn't accept status_filter, so route to the main kanban
        // endpoint's deleted branch (it lifts the date window and
        // returns ALL soft-deleted JCs regardless of pending state).
        url = `${BACKEND_URL}/api/job-cards/kanban?status_filter=deleted&view_type=${viewType}&target_date=${targetDate.toISOString()}`;
        if (searchQuery.trim()) {
          url += `&search=${encodeURIComponent(searchQuery.trim())}`;
        }
      } else if (pendingMode) {
        url = `${BACKEND_URL}/api/job-cards/kanban-pending-previous-weeks`;
        // Pending mode also accepts a search param so the SM can narrow
        // the previous-weeks list to one customer.
        if (searchQuery.trim()) {
          url += `?search=${encodeURIComponent(searchQuery.trim())}`;
        }
      } else {
        // When the SM types a search OR enables the Wallet view, IGNORE
        // the daily/weekly date scope and search across the entire
        // database — that's what you actually want when a customer calls
        // OR when balancing the workshop's full load across mechanics.
        // The backend honours `view_type=custom` with a wide-open range
        // to give us this.
        const isSearching = !!searchQuery.trim();
        const isWalletView = boardMode === 'wallet';
        if (isSearching || isWalletView) {
          // Force a wide custom range so we surface every active JC.
          const wideStart = new Date('2000-01-01T00:00:00Z');
          const wideEnd = new Date('2099-12-31T23:59:59Z');
          url = `${BACKEND_URL}/api/job-cards/kanban?view_type=custom`
            + `&start_date=${wideStart.toISOString()}`
            + `&end_date=${wideEnd.toISOString()}`;
          if (isSearching) {
            url += `&search=${encodeURIComponent(searchQuery.trim())}`;
          }
        } else {
          url = `${BACKEND_URL}/api/job-cards/kanban?view_type=${viewType}`;
          if (viewType === 'custom' && customStartDate && customEndDate) {
            url += `&start_date=${customStartDate.toISOString()}&end_date=${customEndDate.toISOString()}`;
          } else {
            url += `&target_date=${targetDate.toISOString()}`;
          }
        }
        // Add service type filter (applies to both search + normal modes)
        if (serviceTypeFilter && serviceTypeFilter !== 'all') {
          url += `&service_type=${serviceTypeFilter}`;
        }
        // Status scope filter
        if (statusScope && statusScope !== 'all') {
          url += `&status_filter=${statusScope}`;
        }
        // Mechanic filter — narrows the board to a single tech's JCs.
        if (mechanicFilter && mechanicFilter !== 'all') {
          url += `&assigned_to=${encodeURIComponent(mechanicFilter)}`;
        }
      }
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setColumns(data.columns);
        setTotalCards(data.total_cards);
        setDateRange(data.date_range);

        // Detect newly-assigned JCs landing on the board since the last
        // fetch. Toast the mechanic so they don't miss SM assignments.
        // First fetch primes the seen-set silently (no toast on initial
        // load — only on subsequent polls).
        const currentIds = new Set();
        (data.columns || []).forEach((col) => {
          (col.cards || []).forEach((c) => {
            if (c.job_card_id) currentIds.add(c.job_card_id);
          });
        });
        if (firstFetchDoneRef.current && seenJobCardIdsRef.current) {
          const newIds = [];
          (data.columns || []).forEach((col) => {
            (col.cards || []).forEach((c) => {
              if (c.job_card_id && !seenJobCardIdsRef.current.has(c.job_card_id)) {
                newIds.push(c);
              }
            });
          });
          if (newIds.length === 1) {
            const c = newIds[0];
            toast.success(
              `New job assigned — ${c.vehicle_rego || 'no rego'} · ${c.customer_name || 'customer'}`,
              { duration: 8000 }
            );
          } else if (newIds.length > 1) {
            toast.success(`${newIds.length} new job cards assigned to you`, { duration: 8000 });
          }
        }
        seenJobCardIdsRef.current = currentIds;
        firstFetchDoneRef.current = true;
      }
    } catch (error) {
      console.error('Failed to fetch kanban:', error);
    }
  }, [user, viewType, targetDate, serviceTypeFilter, customStartDate, customEndDate, pendingMode, searchQuery, statusScope, mechanicFilter, boardMode]);

  useEffect(() => {
    fetchKanban();
  }, [fetchKanban]);

  // Same-day duplicate detection — flag JCs sharing rego / phone /
  // customer on the same day so SM spots data-entry mix-ups.
  const [duplicateFlags, setDuplicateFlags] = useState({});
  useEffect(() => {
    let cancelled = false;
    const fetchDupes = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/job-cards/same-day-duplicates`, {
          credentials: 'include',
        });
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) setDuplicateFlags(data.flagged_ids || {});
        }
      } catch { /* noop */ }
    };
    fetchDupes();
    return () => { cancelled = true; };
  }, [columns]);

  // When filters change, reset the "seen JC ids" tracker so the next fetch
  // primes silently rather than toasting every card as "new".
  useEffect(() => {
    firstFetchDoneRef.current = false;
    seenJobCardIdsRef.current = null;
  }, [viewType, targetDate, serviceTypeFilter, customStartDate, customEndDate, searchQuery, statusScope, mechanicFilter, pendingMode]);

  // Auto-refresh the kanban when the SM returns to this tab — covers the
  // case where they edit an invoice in another tab (e.g. pushing quote
  // items back to JC) which flips a JC status server-side, but the kanban
  // is still showing stale tiles. Triggers on tab-focus and window-focus.
  useEffect(() => {
    const handleFocus = () => {
      if (!document.hidden) fetchKanban();
    };
    document.addEventListener('visibilitychange', handleFocus);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchKanban]);

  // Periodic polling so newly-assigned JCs surface on the mechanic's board
  // without a manual refresh. Mechanics poll every 30s; SM/Admin every 60s
  // by default, or every 15s when the Wallet view is active (real-time
  // workshop balancing → fresher data is critical). Skipped when the tab
  // is hidden so we don't burn server CPU on inactive tabs.
  useEffect(() => {
    if (!user) return undefined;
    // iter138.3 — Mechanic JC list polls every 15s (was 30s) so SM/FM
    // re-orderings and OP→PO toggles surface within a 15s window on the
    // mechanic's iPad. Wallet view keeps its 15s rhythm; SM/FM status
    // board stays at 60s.
    const intervalMs = isMechanic ? 15000 : (boardMode === 'wallet' ? 15000 : 60000);
    const id = setInterval(() => {
      if (!document.hidden) fetchKanban();
    }, intervalMs);
    return () => clearInterval(id);
  }, [user, fetchKanban, isMechanic, boardMode]);

  // Fetch workshop labour rate once for the wallet's $ delta calc.
  useEffect(() => {
    if (!user || !canManage) return;
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/super-admin/workshop-settings/labour-rate`, {
          credentials: 'include',
        });
        if (r.ok) {
          const d = await r.json();
          if (!cancel && d?.labour_rate > 0) setLabourRate(Number(d.labour_rate));
        }
      } catch (_e) { /* default 120 */ }
    })();
    return () => { cancel = true; };
  }, [user, canManage]);

  // Eagerly load the mechanic list for SM/admin users so the Mechanic
  // filter dropdown + Wallet view get populated immediately (was only
  // fetched on opening the Create Job Card dialog, which left the Wallet
  // view rendering only the mechanics that had at least one JC instead
  // of every active mechanic in the workshop).
  useEffect(() => {
    if (!user) return;
    if (canManage && mechanics.length === 0) {
      fetchMechanics();
    }
     
  }, [user, canManage]);

  // Related-items panel: when the kanban search box has content, fetch
  // matching invoices / bookings / customers / vehicles from the global
  // search endpoint so the SM has full context on the phone. Debounced
  // so we don't fire on every keystroke.
  useEffect(() => {
    if (!user || isMechanic) { setRelatedItems(null); return undefined; }
    const term = (searchQuery || '').trim();
    if (term.length < 2) { setRelatedItems(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/global-search?q=${encodeURIComponent(term)}&limit_each=8`,
          { credentials: 'include' },
        );
        if (r.ok) setRelatedItems(await r.json());
      } catch (_e) { /* swallow */ }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery, user, isMechanic]);

  // Sync the kanban view-type with the CapacityBanner's period selector.
  // Wrapped in useCallback because it's a dep of the banner's effect —
  // an inline arrow would re-create on every render and loop.
  const handleCapacityPeriodSync = useCallback((period, range) => {
    if (period === 'day') {
      setViewType('daily');
      setTargetDate(new Date());
    } else if (period === 'week') {
      setViewType('weekly');
      setTargetDate(new Date());
    } else if (period === 'month') {
      setViewType('monthly');
      setTargetDate(new Date());
    } else if (period === 'custom' && range?.start && range?.end) {
      setViewType('custom');
      setCustomStartDate(new Date(`${range.start}T00:00:00`));
      setCustomEndDate(new Date(`${range.end}T23:59:59`));
    }
  }, []);

  const fetchMechanics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/mechanics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMechanics(data);
      }
    } catch (error) {
      console.error('Failed to fetch mechanics:', error);
    }
  };

  const fetchBookingsWithoutCards = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/without-job-cards?page_size=50`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBookingsWithoutCards(data.bookings);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const openCreateDialog = async () => {
    await Promise.all([fetchBookingsWithoutCards(), fetchMechanics()]);
    setSelectedBooking(null);
    setSelectedPriority('medium');
    setSelectedMechanic('');
    setShowCreateDialog(true);
  };

  const handleCreateJobCard = async () => {
    if (!selectedBooking) {
      toast.error('Please select a booking');
      return;
    }
    
    setCreateLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          booking_id: selectedBooking,
          priority: selectedPriority,
          assigned_to: selectedMechanic || null
        })
      });

      if (response.ok) {
        const jobCard = await response.json();
        toast.success(`Job Card ${jobCard.job_card_number} created`);
        setShowCreateDialog(false);
        fetchKanban();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create job card');
      }
    } catch (error) {
      console.error('Failed to create job card:', error);
      toast.error('Failed to create job card');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !canDragDrop) return;
    
    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId) return;
    
    const newStatus = destination.droppableId;
    
    // If trying to complete, check if allowed
    if (newStatus === 'completed') {
      try {
        const checkResponse = await fetch(
          `${BACKEND_URL}/api/job-cards/${draggableId}/can-complete`,
          { credentials: 'include' }
        );
        const checkData = await checkResponse.json();
        
        if (!checkData.can_complete) {
          toast.error(`Cannot complete. Missing: ${checkData.missing_items.join(', ')}`);
          return;
        }
      } catch (error) {
        toast.error('Failed to check completion status');
        return;
      }
    }

    // Optimistically update UI. Use the FUNCTIONAL setState form so that
    // rapid successive drags compose correctly — previously the closure
    // captured a stale `columns` snapshot and the second drag could
    // visually orphan or duplicate cards. We also de-dupe by job_card_id
    // when appending to the destination so the tile NEVER renders twice
    // for the same JC even if the server briefly returns it in two
    // columns.
    setColumns(prev => prev.map(col => {
      if (col.status === source.droppableId) {
        return {
          ...col,
          cards: col.cards.filter(c => c.job_card_id !== draggableId),
          count: Math.max(0, col.count - 1),
        };
      }
      if (col.status === destination.droppableId) {
        const movedCard = prev
          .find(c => c.status === source.droppableId)
          ?.cards.find(c => c.job_card_id === draggableId);
        if (movedCard) {
          // Drop any pre-existing entry with the same id before appending —
          // protects against the case where the server's previous response
          // already had the card in destination (e.g. due to a flaky
          // request) and avoids two tiles binding to the same JC.
          const cleanedDest = col.cards.filter(c => c.job_card_id !== draggableId);
          return {
            ...col,
            cards: [...cleanedDest, { ...movedCard, status: newStatus }],
            count: cleanedDest.length + 1,
          };
        }
      }
      return col;
    }));
    
    // Update on server — use XHR to bypass the global fetch interceptor
    // so we can read the 409 conflict body reliably.
    try {
      const { status: rStatus, body: rBody } = await xhrJson(
        'PUT', `${BACKEND_URL}/api/job-cards/${draggableId}`, { status: newStatus },
      );

      if (rStatus >= 200 && rStatus < 300) {
        toast.success(`Moved to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
        // ── Reconcile from server ────────────────────────────────────
        // PROD BUG (Jun 2026): SM reported two SM-Review tiles showing
        // identical info after a drag-drop. Root cause turned out to be
        // historical customer_id drift on the JCs, but the trigger was
        // the kanban displaying optimistic state that never got
        // reconciled with the server. Always re-fetch after a successful
        // status change so what's on screen matches the DB exactly.
        // Cheap (single kanban query) and removes a whole class of
        // "ghost tile" bugs.
        fetchKanban();
      } else if (rStatus === 409) {
        // Single-in-progress rule — backend says this mechanic already has
        // another active JC. Open the parking dialog so the user can park
        // the existing one (Pickup [pause] / Waiting Parts / Sublet)
        // before starting this one. Roll back the optimistic UI move.
        if (rBody?.detail?.code === 'MECHANIC_HAS_ACTIVE_JC') {
          setActiveJcConflict({
            active: rBody.detail.active_job_card,
            targetJobCardId: draggableId,
            targetStatus: newStatus,
            message: rBody.detail.message,
          });
          fetchKanban();
        } else {
          fetchKanban();
          toast.error(
            (typeof rBody?.detail === 'string' ? rBody.detail : null)
              || 'Failed to update status',
            { duration: 10000 },
          );
        }
      } else {
        // Non-409 error path. Always surface the BE's contextual message
        // (e.g. odometer missing / checklist incomplete / quoted hours
        // missing / tasks not ticked Done) with a long-duration toast so
        // the mechanic can read it on the workshop floor.
        //
        // If the backend returned no usable `detail`, fall back to a
        // diagnostic that includes the HTTP status code so the SM can
        // forward an actionable error (instead of a generic "failed to
        // update status" with no clue what to fix).
        // Non-409 error path. Always surface the BE's contextual message
        // (e.g. odometer missing / checklist incomplete / quoted hours
        // missing / tasks not ticked Done) with a long-duration toast so
        // the mechanic can read it on the workshop floor.
        //
        // If the backend returned no usable `detail`, fall back to a
        // diagnostic that includes the HTTP status code so the SM can
        // forward an actionable error (instead of a generic "failed to
        // update status" with no clue what to fix).
        const error = rBody || {};
        fetchKanban();
        let beMessage = null;
        if (typeof error?.detail === 'string') {
          beMessage = error.detail;
        } else if (typeof error?.detail?.message === 'string') {
          beMessage = error.detail.message;
        } else if (Array.isArray(error?.detail) && error.detail.length > 0) {
          // Pydantic 422 — show the first validation message in plain words.
          const firstField = error.detail[0]?.loc?.[error.detail[0].loc.length - 1] || 'field';
          beMessage = `Validation error on ${firstField}: ${error.detail[0]?.msg || 'invalid'}.`;
        }
        const msg = beMessage
          || `Couldn't move to ${STATUS_CONFIG[newStatus]?.label || newStatus} (server returned ${rStatus}). If this keeps happening, screenshot this and send to support.`;
        toast.error(msg, { duration: 12000 });
      }
    } catch (error) {
      fetchKanban();
      toast.error('Network error — please check your connection and try again.', { duration: 8000 });
    }
  };

  // Park the current in-progress JC and retry the original transition.
  // Used by the "Mechanic already has an active JC" conflict dialog.
  const handleParkAndRetry = async (parkStatus) => {
    if (!activeJcConflict) return;
    setParkingInFlight(true);
    try {
      // Step 1 — park the currently-active JC.
      const park = await xhrJson(
        'PUT',
        `${BACKEND_URL}/api/job-cards/${activeJcConflict.active.job_card_id}`,
        { status: parkStatus },
      );
      if (park.status < 200 || park.status >= 300) {
        toast.error(
          park.body?.detail?.message
          || park.body?.detail
          || `Failed to park ${activeJcConflict.active.job_card_number}`,
        );
        return;
      }

      // Step 2 — retry the original move.
      const retry = await xhrJson(
        'PUT',
        `${BACKEND_URL}/api/job-cards/${activeJcConflict.targetJobCardId}`,
        { status: activeJcConflict.targetStatus },
      );
      if (retry.status >= 200 && retry.status < 300) {
        const parkLabel = parkStatus === 'pickup' ? 'Dropped-off (clock paused)'
          : parkStatus === 'blocked' ? 'Sublet' : 'Waiting for Parts';
        toast.success(
          `Parked ${activeJcConflict.active.job_card_number} → ${parkLabel}. New job is now in progress.`,
          { duration: 6000 },
        );
        setActiveJcConflict(null);
        fetchKanban();
      } else if (retry.status === 409 && retry.body?.detail?.code === 'MECHANIC_HAS_ACTIVE_JC') {
        // Another in_progress JC still exists for this mechanic — re-open
        // the dialog with the NEW conflict so SM can pause that one too.
        // Common in workshops where pre-existing dirty data has multiple
        // in_progress per mechanic.
        setActiveJcConflict({
          active: retry.body.detail.active_job_card,
          targetJobCardId: activeJcConflict.targetJobCardId,
          targetStatus: activeJcConflict.targetStatus,
          message: retry.body.detail.message,
        });
        fetchKanban();
        toast.warning(
          `Mechanic still has another active job — pause it too.`,
          { duration: 6000 },
        );
      } else {
        setActiveJcConflict(null);
        fetchKanban();
        toast.error(
          retry.body?.detail?.message
          || retry.body?.detail
          || 'Failed to start the new job',
          { duration: 8000 },
        );
      }
    } catch (e) {
      toast.error('Failed to park / retry');
    } finally {
      setParkingInFlight(false);
    }
  };

  const handleCardClick = async (card) => {
    // Fetch fresh data for the card
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${card.job_card_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const fullCard = await response.json();
        setSelectedJobCard(fullCard);
        setShowDetailModal(true);
        if (mechanics.length === 0) {
          fetchMechanics();
        }
      }
    } catch (error) {
      toast.error('Failed to load job card details');
    }
  };

  // Deep-link: if URL has ?id=<job_card_id>, auto-open that card once.
  useEffect(() => {
    const jcId = searchParams.get('id');
    if (!jcId || selectedJobCard) return;
    handleCardClick({ job_card_id: jcId });
     
  }, [searchParams]);

  // Deep-link: ?date=YYYY-MM-DD&service_type=service — set kanban to daily view
  // for that date + service-type filter. Used by Dashboard service tiles.
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const stParam = searchParams.get('service_type');
    if (dateParam) {
      // Anchor at Melbourne noon (date-only, tz-safe) so the backend's
      // Melbourne-day projection lands on the wanted local day regardless
      // of browser tz.
      const [y, m, d] = dateParam.split('-').map(n => parseInt(n, 10));
      if (y && m && d) {
        // Use UTC midday as a safe anchor — both AEDT (+11) and AEST (+10)
        // map 02:00Z and 03:00Z to "today" in Melbourne, so 12:00Z is well
        // inside the local day everywhere.
        const targetD = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
        setViewType('daily');
        setTargetDate(targetD);
      }
    }
    if (stParam) {
      setServiceTypeFilter(stParam);
    }
     
  }, [searchParams]);

  const handleUpdatePriority = async (jobCardId, priority) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ priority })
      });

      if (response.ok) {
        toast.success('Priority updated');
        fetchKanban();
      } else {
        toast.error('Failed to update priority');
      }
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  // iter138.9v — Set / clear Delivery Due date+time from the JC tile
  // 3-dot menu. Replaces the old Low/Medium/Urgent priority picker.
  // Pass `null` to clear. Date is stored as ISO 8601 string.
  const handleUpdateDeliveryDue = async (jobCardId, deliveryDueAtIso) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ delivery_due_at: deliveryDueAtIso })
      });

      if (response.ok) {
        toast.success(deliveryDueAtIso ? 'Delivery due updated' : 'Delivery due cleared');
        fetchKanban();
      } else {
        const e = await response.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to update delivery due');
      }
    } catch (error) {
      toast.error('Failed to update delivery due');
    }
  };

  const handleAssignMechanic = async (jobCardId, mechanicId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ assigned_to: mechanicId })
      });

      if (response.ok) {
        toast.success('Mechanic assigned');
        fetchKanban();
      } else {
        // iter138.6 — surface the BE's structured error so SM sees the
        // exact reason (e.g. allocated-hours-required for which car).
        const err = await response.json().catch(() => ({}));
        const detail = err?.detail;
        if (detail && typeof detail === 'object' && detail.code === 'ALLOCATED_HOURS_REQUIRED') {
          toast.error(detail.message, { duration: 10000 });
        } else if (typeof detail === 'string') {
          toast.error(detail, { duration: 8000 });
        } else {
          toast.error('Failed to assign mechanic');
        }
      }
    } catch (error) {
      toast.error('Failed to assign mechanic');
    }
  };

  const handleDeleteJobCard = async (jobCardId, jobCardNumber) => {
    if (!window.confirm(`Delete job card ${jobCardNumber}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${jobCardId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Job card deleted');
        fetchKanban();
      } else {
        toast.error('Failed to delete job card');
      }
    } catch (error) {
      toast.error('Failed to delete job card');
    }
  };

  // iter126: Recover an accidentally-deleted JC. Restores it to its
  // previous status (whatever lane it was in before the delete) and
  // re-links the booking. Idempotent — re-clicking on an already-active
  // JC just returns `already_active: true`.
  const handleRecoverJobCard = async (jobCardId, jobCardNumber, prevStatus) => {
    if (!window.confirm(`Recover ${jobCardNumber}? It will be restored to "${prevStatus || 'previous'}" status.`)) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCardId}/recover`,
        { method: 'POST', credentials: 'include' },
      );
      if (response.ok) {
        const d = await response.json();
        toast.success(`✓ Recovered → restored to "${d.restored_status || 'previous'}" status`);
        fetchKanban();
      } else {
        const e = await response.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to recover job card');
      }
    } catch (error) {
      toast.error('Failed to recover job card');
    }
  };

  const handleCreateInvoice = async (card) => {
    // Use the new 1:1 create-invoice endpoint. Idempotent — re-clicking just
    // returns the existing invoice_id with created:false.
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/${card.job_card_id}/create-invoice`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || (data.created ? 'Invoice created' : 'Opening existing invoice'));
        // Anchor-click pattern → opens a new tab AND leaves the current
        // /job-cards page intact (window.open + noopener returns null in
        // Chromium, which would fire a misleading "popup blocked" fallback).
        openInNewTab(`/invoices/${data.invoice_id}`);
        // Refresh kanban so the new invoice_id stamp + green tick render
        fetchKanban && fetchKanban();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to create invoice');
      }
    } catch (_error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleCarryForward = async (card, e) => {
    e?.stopPropagation?.();
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${card.job_card_id}/carry-forward`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success(`Carried forward "${card.title || 'job'}" to this week`);
        fetchKanban();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to carry forward');
      }
    } catch (_e) {
      toast.error('Failed to carry forward');
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(targetDate);
    switch (viewType) {
      case 'daily':
        newDate.setDate(newDate.getDate() + direction);
        break;
      case 'weekly':
        newDate.setDate(newDate.getDate() + (7 * direction));
        break;
      case 'monthly':
        newDate.setMonth(newDate.getMonth() + direction);
        break;
      case 'yearly':
        newDate.setFullYear(newDate.getFullYear() + direction);
        break;
    }
    setTargetDate(newDate);
  };

  // Jump straight to a specific day picked from the date input. SM no
  // longer needs to click "Today" then ◀/▶ repeatedly to walk to the
  // target day. Picking a date also flips the view to Daily so the
  // kanban shows that exact day's board.
  const jumpToDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return;
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    if (!y || !m || !d) return;
    setTargetDate(new Date(y, m - 1, d));
    if (viewType !== 'daily') setViewType('daily');
  };

  // YYYY-MM-DD value (Melbourne local) for the date input — mirrors
  // whatever targetDate currently points at so the picker reflects the
  // active board.
  const targetDateAsInput = (() => {
    const d = targetDate;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  })();

  const formatDateRange = () => {
    if (!dateRange.start) return '';

    const options = { day: 'numeric', month: 'short', year: 'numeric' };

    switch (viewType) {
      case 'daily':
        return formatMelbourneDate(dateRange.start, { weekday: 'long', ...options });
      case 'weekly':
        return `${formatMelbourneDate(dateRange.start, { day: 'numeric', month: 'short' })} - ${formatMelbourneDate(dateRange.end, options)}`;
      case 'monthly':
        return formatMelbourneDate(dateRange.start, { month: 'long', year: 'numeric', day: undefined });
      case 'yearly':
        return formatMelbourneDate(dateRange.start, { year: 'numeric', month: undefined, day: undefined });
      default:
        return '';
    }
  };

  const getCardSize = () => {
    // Use consistent card size across all views for readability
    // User requested: "same job card size, neat and very readable"
    switch (viewType) {
      case 'daily': return 'large';
      case 'weekly': return 'large';
      case 'monthly': return 'medium';
      case 'yearly': return 'compact';
      case 'custom': return 'large';  // Custom range uses large cards
      default: return 'large';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const cardSize = getCardSize();

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8 h-full flex flex-col" data-testid="job-cards-container">
        {/* Header — title + primary controls all on the LEFT so SMs don't
            have to scroll/look right to reach Create / view / date nav.
            On narrow screens the controls wrap below the title cleanly.
            Star Performer pill (iter178) sits at the far RIGHT of this row. */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="font-['Chivo'] font-bold text-3xl sm:text-4xl text-slate-900">
              {pendingMode ? 'Pending from Previous Weeks' : 'Job Cards'}
            </h1>

            {pendingMode ? (
              <Button
                variant="outline"
                onClick={() => navigate('/job-cards')}
                className="h-10"
                data-testid="exit-pending-mode"
              >
                <X className="h-4 w-4 mr-1" />Exit Review
              </Button>
            ) : (
              <>
                {canManage && (
                  <Button
                    onClick={() => setShowQuickBooking(true)}
                    className="h-10 px-4 bg-blue-600 hover:bg-blue-700"
                    data-testid="create-job-card-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Job Card
                  </Button>
                )}

                <Select
                  value={viewType}
                  onValueChange={(v) => {
                    setViewType(v);
                    // When SM switches to Custom, immediately pre-fill the
                    // dates to "last 6 months → today" so the kanban
                    // shows something useful right away instead of
                    // silently falling back to today's view. SM can then
                    // narrow the range; both fields auto-trigger a refetch
                    // via the fetchKanban dep array.
                    if (v === 'custom' && (!customStartDate || !customEndDate)) {
                      const end = new Date();
                      const start = new Date();
                      start.setMonth(start.getMonth() - 6);
                      start.setHours(0, 0, 0, 0);
                      end.setHours(23, 59, 59, 999);
                      setCustomStartDate(start);
                      setCustomEndDate(end);
                    }
                  }}
                >
                  <SelectTrigger className="h-10 w-32" data-testid="view-type-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VIEW_TYPES.map(vt => (
                      <SelectItem key={vt.value} value={vt.value}>{vt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Board-mode toggle — Status kanban vs Mechanic Wallet.
                    Hidden for mechanic users (they only need to see their
                    own work, not other mechanics). */}
                {!isMechanic && !pendingMode && (
                  <div className="inline-flex bg-white border rounded-lg p-0.5 h-10 items-center">
                    <Button
                      variant={boardMode === 'status' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setBoardMode('status')}
                      className="h-8 px-3 text-xs"
                      data-testid="board-mode-status"
                    >
                      Status
                    </Button>
                    <Button
                      variant={boardMode === 'wallet' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setBoardMode('wallet')}
                      className="h-8 px-3 text-xs"
                      data-testid="board-mode-wallet"
                      title="Group job cards by mechanic — see at-a-glance who's working on what."
                    >
                      Wallet
                    </Button>
                  </div>
                )}

                <div className="flex items-center gap-1 bg-white border rounded-lg p-1 h-10">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigateDate(-1)}
                    className="h-7 w-7"
                    data-testid="jc-nav-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTargetDate(new Date())}
                    className="text-sm px-2 h-7"
                    data-testid="jc-today-btn"
                  >
                    Today
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigateDate(1)}
                    className="h-7 w-7"
                    data-testid="jc-nav-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {/* Jump-to-date — SM picks any day and the board jumps
                    straight there in Daily view. No more pressing Today
                    + ▶ repeatedly to reach a specific date. */}
                <div className="flex items-center gap-2 bg-white border rounded-lg px-2 h-10">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  <Input
                    type="date"
                    value={targetDateAsInput}
                    onChange={(e) => jumpToDate(e.target.value)}
                    className="h-7 w-36 border-0 p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                    data-testid="jc-date-picker"
                    aria-label="Jump to a specific date"
                  />
                </div>
                {/* Star Performer of the week — FULL banner, inline right
                    next to the calendar filter (iter180 — user wants the
                    earlier banner size on the title row, not a compact
                    pill). */}
                <div data-testid="star-performer-slot">
                  <StarPerformerBanner inline />
                </div>

                {/* iter140 — On-Leave banner: shows every APPROVED leave at
                    the site whose end_date ≥ today, regardless of whether
                    it overlaps the current kanban date range. SM/FM use this
                    to plan capacity (who's off, when), so it MUST stay
                    visible even when SM is browsing past dates. */}
                <OnLeaveBanner variant="inline" />
              </>
            )}
          </div>

          <p className="text-slate-500">
            {pendingMode ? (
              <>
                <span data-testid="pending-total-count">{totalCards}</span> job card{totalCards !== 1 ? 's' : ''} still open from before this week — review, update or carry forward.
              </>
            ) : searchQuery.trim() ? (
              <>
                {totalCards} match{totalCards !== 1 ? 'es' : ''} for "{searchQuery.trim()}"
                <span
                  className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5"
                  title="Search ignores the date filter so we hit every job card in the system — no need to widen the range manually."
                  data-testid="search-wide-scope-badge"
                >
                  searching all time
                </span>
              </>
            ) : viewType === 'custom' ? (
              <>
                {totalCards} job card{totalCards !== 1 ? 's' : ''} • {formatDateRange()}
                <span
                  className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5"
                  title="Custom range surfaces every still-active JC (pickup / in_progress / waiting_for_parts / blocked / sm_review) regardless of date so nothing pending falls through the cracks."
                  data-testid="custom-range-lift-badge"
                >
                  + all active JCs
                </span>
              </>
            ) : (
              <>{totalCards} job card{totalCards !== 1 ? 's' : ''} • {formatDateRange()}</>
            )}
          </p>
        </div>

        {/* Capacity banner — sums quoted labour hours across active JCs
            in the selected range. Hidden in pending-prev-weeks audit
            view since it'd skew the totals. */}
        {!pendingMode && (
          <CapacityBanner
            getSiteParams={getSiteParams}
            onPeriodSync={handleCapacityPeriodSync}
          />
        )}

        {/* Pending-mode top toolbar — gives the SM the same search +
            status filter while reviewing carry-forward candidates. Before
            this, the search bar was hidden in pending mode, so users
            typed in the GLOBAL header search instead — which doesn't
            filter the kanban (that was the second reported bug). */}
        {pendingMode && (
          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-rose-50/40 rounded-lg border border-rose-200">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-rose-500" />
              <span className="text-sm font-medium text-rose-700">Filter pending JCs:</span>
            </div>
            <Select value={statusScope} onValueChange={setStatusScope}>
              <SelectTrigger className="w-40 bg-white" data-testid="jc-status-filter-pending">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="booking">Booking</SelectItem>
                {isSuperAdmin && <SelectItem value="no_show">No Show</SelectItem>}
                <SelectItem value="pickup">Dropped-off</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="waiting_for_parts">Waiting Parts</SelectItem>
                <SelectItem value="blocked">Sublet</SelectItem>
                <SelectItem value="sm_review">SM Review</SelectItem>
                {/* iter126: Deleted view — SM/SA can find accidentally-
                    deleted JCs grouped by their ORIGINAL status, with a
                    one-click Recover button on each card. */}
                <SelectItem value="deleted" data-testid="status-filter-deleted">🗑️ Deleted (recoverable)</SelectItem>
              </SelectContent>
            </Select>
            <JobCardSearchAutoComplete
              value={searchQuery}
              onChange={setSearchQuery}
              onPickIdentifier={(val) => setSearchQuery(val)}
              onOpenJobCard={(id) => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('id', id);
                  return next;
                });
              }}
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery('')}
                className="text-rose-600"
                data-testid="pending-clear-search"
              >
                Clear search
              </Button>
            )}
          </div>
        )}

        {/* Filters Row */}
        {!pendingMode && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg border">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Filters:</span>
          </div>
          
          <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
            <SelectTrigger className="w-44 bg-white" data-testid="service-type-filter">
              <SelectValue placeholder="Service Type" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_TYPE_FILTERS.map(st => (
                <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status scope — lets SA quickly filter to No Shows / Picked Up
              / Booking etc. without changing date range. */}
          <Select value={statusScope} onValueChange={setStatusScope}>
            <SelectTrigger className="w-40 bg-white" data-testid="jc-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {isSuperAdmin && <SelectItem value="no_show">⚠ No Show</SelectItem>}
              <SelectItem value="booking">Booking (awaiting customer)</SelectItem>
              <SelectItem value="pickup">Dropped-off</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="waiting_for_parts">Waiting Parts</SelectItem>
              <SelectItem value="blocked">Sublet</SelectItem>
              <SelectItem value="sm_review">SM Review</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              {/* iter126: Deleted view also exposed in the main filters row
                  so SM can recover JCs without entering Pending mode. */}
              <SelectItem value="deleted" data-testid="status-filter-deleted-main">🗑️ Deleted (recoverable)</SelectItem>
            </SelectContent>
          </Select>

          {/* Mechanic filter — lets the SM scope the entire board to one
              tech's JCs so they can see what they're working on across
              every column. Hidden for mechanic users (they're already
              auto-filtered to themselves). */}
          {!isMechanic && (
            <Select value={mechanicFilter} onValueChange={setMechanicFilter}>
              <SelectTrigger className="w-44 bg-white" data-testid="jc-mechanic-filter">
                <SelectValue placeholder="Mechanic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mechanics</SelectItem>
                {mechanics.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Search box — rego / customer name / mobile suffix. */}
          <JobCardSearchAutoComplete
            value={searchQuery}
            onChange={setSearchQuery}
            onPickIdentifier={(val) => setSearchQuery(val)}
            onOpenJobCard={(id) => {
              // Use the existing ?id=… deep-link convention — the page
              // already hydrates the detail modal from the URL param.
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('id', id);
                return next;
              });
            }}
          />
          
          {viewType === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStartDate ? customStartDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setCustomStartDate(e.target.value ? new Date(e.target.value) : null)}
                className="w-36 bg-white"
                placeholder="Start Date"
                data-testid="custom-start-date"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={customEndDate ? customEndDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setCustomEndDate(e.target.value ? new Date(e.target.value) : null)}
                className="w-36 bg-white"
                placeholder="End Date"
                data-testid="custom-end-date"
              />
            </div>
          )}
          
          {(serviceTypeFilter !== 'all' || viewType === 'custom') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setServiceTypeFilter('all');
                setViewType('daily');
                setCustomStartDate(null);
                setCustomEndDate(null);
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          )}
          
          {/* iter139.1 — Show/Hide Quote column toggle. SM uses this
              to keep the operational kanban clean by default, but flip
              ON when reviewing outstanding quote follow-ups. */}
          <Button
            type="button"
            variant={showQuoteColumn ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowQuoteColumn(v => !v)}
            className={showQuoteColumn ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'}
            data-testid="jc-toggle-quote-column"
            title="Show / hide the Quote column on the kanban"
          >
            {showQuoteColumn ? '✓ Quotes' : 'Quotes'}
          </Button>

          {/* Filter Preset Manager */}
          <div className="ml-auto">
            <FilterPresetManager 
              page="job_cards"
              currentFilters={{
                viewType,
                serviceTypeFilter,
                customStartDate: customStartDate?.toISOString(),
                customEndDate: customEndDate?.toISOString()
              }}
              onLoadPreset={(filters) => {
                if (filters.viewType) setViewType(filters.viewType);
                if (filters.serviceTypeFilter) setServiceTypeFilter(filters.serviceTypeFilter);
                if (filters.customStartDate) setCustomStartDate(new Date(filters.customStartDate));
                if (filters.customEndDate) setCustomEndDate(new Date(filters.customEndDate));
              }}
            />
          </div>
        </div>
        )}

        {/* Pending-mode banner */}
        {pendingMode && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-3" data-testid="pending-banner">
            <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-rose-900">
              <p className="font-semibold">These job cards were created before this week and are still open.</p>
              <p className="text-rose-700 mt-0.5">
                Drag a card between columns to update progress. Open a card to add comments. Click <ArrowRightCircle className="inline h-3.5 w-3.5" /> <span className="font-medium">Carry Forward</span> on a card to move its booked date to today and pull it into the current week.
              </p>
            </div>
          </div>
        )}

        {/* iter126: Banner explaining the deleted-recovery view so SM
            doesn't get confused — the lanes look identical to the
            normal kanban but the cards inside are all soft-deleted. */}
        {statusScope === 'deleted' && (
          <div
            className="mb-3 p-3 rounded-lg border-2 border-rose-200 bg-rose-50/70 flex items-start gap-3"
            data-testid="deleted-recovery-banner"
          >
            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-rose-500 text-white flex items-center justify-center text-lg">🗑️</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-rose-900 text-sm">Deleted job cards — recovery view</p>
              <p className="text-xs text-rose-700 mt-0.5">
                Showing cards that were soft-deleted. Each tile is grouped by the status it had BEFORE deletion. Click the green
                <span className="inline-block px-1.5 py-0.5 mx-1 rounded bg-emerald-600 text-white text-[10px] font-bold">↻ Recover</span>
                button on any tile to restore it to that exact state. Drag-and-drop is disabled here — switch back to <span className="font-semibold">All Statuses</span> to manage active JCs.
              </p>
            </div>
          </div>
        )}

        {/* Kanban Board OR Mechanic Wallet — same data, two layouts.
            Status kanban (default) groups by status; Wallet groups by
            mechanic with status chips so SM can balance workload. */}
        {boardMode === 'wallet' && !isMechanic && !pendingMode ? (
          <MechanicWalletBoard
            columns={columns}
            mechanics={mechanics}
            onCardClick={(card) => handleCardClick(card)}
            onChange={fetchKanban}
            labourRate={labourRate}
            canDrag={canManage}
            onActiveJcConflict={setActiveJcConflict}
            user={user}
            onFetchMechanics={fetchMechanics}
            onUpdatePriority={handleUpdatePriority}
            onUpdateDeliveryDue={handleUpdateDeliveryDue}
            onAssignMechanic={handleAssignMechanic}
            onDelete={handleDeleteJobCard}
            onCreateInvoice={handleCreateInvoice}
            showQuoteColumn={showQuoteColumn}
          />
        ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          {pendingMode && totalCards === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md py-12" data-testid="pending-empty">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 mb-4">
                  <ArrowRightCircle className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">All caught up</h2>
                <p className="text-sm text-slate-500">
                  No job cards are pending from previous weeks. Nice work keeping the workshop on track.
                </p>
              </div>
            </div>
          ) : isMechanic && totalCards === 0 ? (
            /* Helpful empty state for mechanics — most common cause of
               "I can't see my job cards" is auth/site mismatch or the
               SM hasn't actually saved the assignment yet. Surface
               actionable next-step hints instead of a blank screen. */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md py-12" data-testid="mechanic-empty">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-50 mb-4">
                  <ArrowRightCircle className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">No job cards yet</h2>
                <p className="text-sm text-slate-500 mb-4">
                  When the Service Manager assigns a job to you, it appears here automatically — usually within 30 seconds.
                </p>
                <Button
                  variant="outline"
                  onClick={() => fetchKanban()}
                  data-testid="mechanic-refresh"
                  className="text-sm"
                >
                  Refresh now
                </Button>
                <p className="text-[11px] text-slate-400 mt-3">
                  Logged in as <strong>{user?.name || user?.email}</strong> (<code className="text-[10px]">{user?.user_id}</code>)
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  If you have jobs assigned but don't see them, ask your Service Manager to re-open the job and re-assign you.
                </p>
              </div>
            </div>
          ) : (
          <div className="flex-1 overflow-x-auto">
            <div className="flex gap-4 min-w-max h-full pb-4">
              {columns
                .filter((column) => isSuperAdmin || column.status !== 'no_show')
                .filter((column) => showQuoteColumn || column.status !== 'quote')
                .map((column) => {
                const displayCards = displayCardsFor(column);
                const ui = sectionUiFor(column.status);
                const supportsBulkSms = ['booking', 'quote', 'no_show'].includes(column.status);
                const selIds = supportsBulkSms ? selectedIdsFor[column.status] : new Set();
                const setSelIds = supportsBulkSms ? setSelectedIdsFor[column.status] : null;
                return (
                <div 
                  key={column.status} 
                  className={`flex flex-col ${viewType === 'yearly' ? 'w-48' : viewType === 'monthly' ? 'w-56' : 'w-72'} flex-shrink-0`}
                >
                  <div className={`flex items-center justify-between p-3 rounded-t-lg ${STATUS_CONFIG[column.status]?.bgColor || 'bg-slate-50'} border ${STATUS_CONFIG[column.status]?.borderColor || 'border-slate-200'} border-b-0`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG[column.status]?.color || 'bg-slate-400'}`} />
                      <span className="font-semibold text-slate-800">{column.label}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => toggleSectionSort(column.status)}
                        className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-600 hover:text-slate-900 bg-white/70 hover:bg-white px-1.5 py-0.5 rounded border border-white/60"
                        title={`Sort by date ${ui.sortDir === 'asc' ? 'ascending' : 'descending'} (click to toggle)`}
                        data-testid={`section-sort-${column.status}`}
                      >
                        {ui.sortDir === 'asc'
                          ? <ArrowUp className="h-3 w-3" />
                          : <ArrowDown className="h-3 w-3" />}
                        Date
                      </button>
                      <Badge variant="secondary" className="bg-white/80">
                        {displayCards.length !== column.count
                          ? `${displayCards.length}/${column.count}`
                          : column.count}
                      </Badge>
                    </div>
                  </div>

                  {/* iter147 — Per-section search bar. Filters ONLY this column
                      by customer name / phone / rego / make / model / JC number. */}
                  <div className="flex items-center gap-1.5 px-2 py-1.5 bg-white border-x border-slate-200">
                    <Search className="h-3 w-3 text-slate-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={ui.search}
                      onChange={(e) => setSectionSearch(column.status, e.target.value)}
                      placeholder="Name / phone / rego / vehicle"
                      className="flex-1 min-w-0 text-[11px] bg-transparent outline-none placeholder:text-slate-400 text-slate-700"
                      data-testid={`section-search-${column.status}`}
                    />
                    {ui.search && (
                      <button
                        type="button"
                        onClick={() => setSectionSearch(column.status, '')}
                        className="text-slate-400 hover:text-slate-700"
                        data-testid={`section-search-clear-${column.status}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  {/* iter141 + iter147 — Bulk SMS bar for Booking + Quote + No Show columns. */}
                  {supportsBulkSms && canManage && displayCards.length > 0 && (
                    <div
                      className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 bg-sky-50/60 border-x border-sky-200"
                      data-testid={`status-${column.status}-bulk-sms-bar`}
                    >
                      <label className="inline-flex items-center gap-1 cursor-pointer select-none text-slate-700 hover:text-slate-900">
                        <input
                          type="checkbox"
                          checked={displayCards.length > 0 && displayCards.every((c) => selIds.has(c.job_card_id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelIds(new Set(displayCards.map((c) => c.job_card_id)));
                            } else {
                              setSelIds(new Set());
                            }
                          }}
                          className="h-3.5 w-3.5"
                          data-testid={`status-${column.status}-select-all`}
                        />
                        {selIds.size === displayCards.length && displayCards.length > 0
                          ? 'All'
                          : `${selIds.size}/${displayCards.length}`}
                      </label>
                      <button
                        type="button"
                        disabled={selIds.size === 0}
                        onClick={guardComms(() => { setBulkSmsSource(column.status); setBulkSmsOpen(true); })}
                        className={`ml-auto px-2 py-0.5 rounded text-[10px] font-semibold border transition ${
                          selIds.size === 0
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700'
                        }`}
                        data-testid={`status-${column.status}-send-bulk-sms`}
                      >
                        Send SMS ({selIds.size})
                      </button>
                    </div>
                  )}

                  <Droppable droppableId={column.status} isDropDisabled={!canDragDrop}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 p-2 rounded-b-lg border ${STATUS_CONFIG[column.status]?.borderColor || 'border-slate-200'} border-t-0 min-h-[200px] transition-colors ${
                          snapshot.isDraggingOver ? 'bg-blue-50' : 'bg-slate-50/50'
                        }`}
                      >
                        <div className="space-y-2">
                          {displayCards.map((card, index) => (
                            <Draggable 
                              key={card.job_card_id} 
                              draggableId={card.job_card_id} 
                              index={index}
                              isDragDisabled={!canDragDrop}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => handleCardClick(card)}
                                  className={`relative ${supportsBulkSms && selIds.has(card.job_card_id) ? 'ring-2 ring-indigo-300 ring-offset-1 rounded-md' : ''}`}
                                >
                                  {/* iter141 + iter147 — Per-tile checkbox for bulk SMS in Booking/Quote/NoShow. */}
                                  {supportsBulkSms && canManage && (
                                    <label
                                      onClick={(e) => e.stopPropagation()}
                                      className="absolute z-20 top-2 left-2 inline-flex items-center justify-center h-5 w-5 rounded bg-white border border-slate-300 shadow-sm cursor-pointer hover:border-indigo-500"
                                      title="Select for bulk SMS"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selIds.has(card.job_card_id)}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          setSelIds((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(card.job_card_id)) next.delete(card.job_card_id);
                                            else next.add(card.job_card_id);
                                            return next;
                                          });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-3.5 w-3.5 cursor-pointer"
                                        data-testid={`status-${column.status}-checkbox-${card.job_card_id}`}
                                      />
                                    </label>
                                  )}
                                  {pendingMode && canManage && statusScope !== 'deleted' && (
                                    <button
                                      type="button"
                                      onClick={(e) => handleCarryForward(card, e)}
                                      className="absolute top-2 right-2 z-10 flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded-full shadow"
                                      data-testid={`carry-forward-${card.job_card_id}`}
                                      title="Carry forward to current week"
                                    >
                                      <ArrowRightCircle className="h-3 w-3" /> Carry Forward
                                    </button>
                                  )}
                                  <div className="relative">
                                    <JobCardItem 
                                      card={card} 
                                      duplicateFlags={duplicateFlags[card.job_card_id]}
                                      size={cardSize}
                                      canManage={canManage}
                                      user={user}
                                      mechanics={mechanics}
                                      onFetchMechanics={fetchMechanics}
                                      onUpdatePriority={handleUpdatePriority}
                                      onUpdateDeliveryDue={handleUpdateDeliveryDue}
                                      onAssignMechanic={handleAssignMechanic}
                                      onDelete={handleDeleteJobCard}
                                      onCreateInvoice={handleCreateInvoice}
                                      onUpdate={fetchKanban}
                                      isDragging={snapshot.isDragging}
                                    />
                                    {/* iter126: Recover overlay shown ONLY when
                                        the SM is browsing the "Deleted" status
                                        view. Distinct emerald colour so it stands
                                        apart from the regular kebab/menu actions. */}
                                    {statusScope === 'deleted' && card.deleted_at && (
                                      <div
                                        className="absolute bottom-2 right-2 z-10"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRecoverJobCard(
                                              card.job_card_id,
                                              card.job_card_number,
                                              card.status,
                                            );
                                          }}
                                          className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow flex items-center gap-1"
                                          data-testid={`recover-jc-${card.job_card_id}`}
                                          title={`Restore ${card.job_card_number} to "${card.status}" status`}
                                        >
                                          ↻ Recover
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </div>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
                );
              })}
            </div>
          </div>
          )}
        </DragDropContext>
        )}

        {/* Related-items panel — shows bookings / invoices / customers /
            vehicles matching the same search term so the SM has full
            context while on the phone. Auto-hidden when search is empty. */}
        <RelatedItemsPanel
          results={relatedItems}
          currentTerm={searchQuery}
          primaryType="job_cards"
        />

        {/* New Booking popup — Create Job Card button opens the same quick
            booking flow as the New Booking widget (iter253, user request) */}
        <QuickBookingModal
          open={showQuickBooking}
          onClose={() => setShowQuickBooking(false)}
          onCreated={() => setShowQuickBooking(false)}
          getSiteParams={getSiteParams}
        />

        {/* Create Job Card Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Job Card</DialogTitle>
              <DialogDescription>
                Select a booking to create a job card from.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Booking *</label>
                {bookingsWithoutCards.length === 0 ? (
                  <p className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg text-center">
                    No bookings available. All bookings already have job cards.
                  </p>
                ) : (
                  <Select value={selectedBooking || undefined} onValueChange={setSelectedBooking}>
                    <SelectTrigger data-testid="booking-select">
                      <SelectValue placeholder="Choose a booking..." />
                    </SelectTrigger>
                    <SelectContent>
                      {bookingsWithoutCards.map(booking => (
                        <SelectItem key={booking.booking_id} value={booking.booking_id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{booking.booking_number}</span>
                            <span className="text-slate-500">•</span>
                            <span className="text-slate-600">{booking.customer_name || 'Unknown'}</span>
                            {booking.vehicle_rego && (
                              <>
                                <span className="text-slate-500">•</span>
                                <span className="text-slate-600">{booking.vehicle_rego}</span>
                              </>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Priority</label>
                <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          {config.icon && <config.icon className="h-4 w-4" />}
                          <span>{config.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Assign Mechanic (Optional)</label>
                <Select value={selectedMechanic || 'unassigned'} onValueChange={(val) => setSelectedMechanic(val === 'unassigned' ? '' : val)}>
                  <SelectTrigger data-testid="mechanic-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {mechanics.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateJobCard} 
                disabled={!selectedBooking || createLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {createLoading ? 'Creating...' : 'Create Job Card'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Job Card Detail Modal */}
        {selectedJobCard && (
          <JobCardDetailModal
            key={selectedJobCard.job_card_id}
            jobCard={selectedJobCard}
            open={showDetailModal}
            onOpenChange={setShowDetailModal}
            user={user}
            mechanics={mechanics}
            onUpdate={() => {
              fetchKanban();
              // Refresh the selected card
              handleCardClick(selectedJobCard);
            }}
            canManage={canManage}
            onActiveJcConflict={setActiveJcConflict}
          />
        )}

        {/* Single-In-Progress conflict dialog — fired by 409
            MECHANIC_HAS_ACTIVE_JC. Lets the SM/mechanic park their
            current in-progress JC (→ Pickup [recommended, pauses clock]
            or Blocked / Waiting for Parts) and then automatically retries
            the original transition.
        */}
        <Dialog
          open={!!activeJcConflict}
          onOpenChange={(o) => { if (!o) setActiveJcConflict(null); }}
        >
          <DialogContent data-testid="active-jc-conflict-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
                Pause the current job?
              </DialogTitle>
              <DialogDescription>
                {activeJcConflict?.active?.assigned_mechanic_name
                  ? `${activeJcConflict.active.assigned_mechanic_name} is currently working on:`
                  : 'This mechanic is currently working on:'}
              </DialogDescription>
            </DialogHeader>
            {activeJcConflict && (
              <div className="space-y-3">
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">
                    Currently in progress
                  </p>
                  <p className="text-base font-extrabold text-slate-900 mt-1 uppercase tracking-wide">
                    {activeJcConflict.active?.vehicle_rego || 'NO REGO'}
                  </p>
                  {(activeJcConflict.active?.vehicle_make || activeJcConflict.active?.vehicle_model) && (
                    <p className="text-base font-extrabold text-slate-800 uppercase tracking-wide">
                      {[activeJcConflict.active.vehicle_make, activeJcConflict.active.vehicle_model].filter(Boolean).join(' ')}
                    </p>
                  )}
                  {activeJcConflict.active?.customer_name && (
                    <p className="text-xs text-slate-600 mt-1">{activeJcConflict.active.customer_name}</p>
                  )}
                  {activeJcConflict.active?.job_card_number && (
                    <p className="text-[10px] text-slate-500">{activeJcConflict.active.job_card_number}</p>
                  )}
                </div>
                <p className="text-sm text-slate-700 font-medium">
                  Pause this job and start the new one?
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {/* Primary action — pause to Pickup (clock pauses, mechanic
                      can resume later). User explicitly requested this as
                      the default swap action. */}
                  <Button
                    className="justify-start bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    disabled={parkingInFlight}
                    onClick={() => handleParkAndRetry('pickup')}
                    data-testid="park-pickup-btn"
                  >
                    Pause → Dropped-off (clock pauses)
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start border-amber-300 text-amber-800 hover:bg-amber-50"
                    disabled={parkingInFlight}
                    onClick={() => handleParkAndRetry('waiting_for_parts')}
                    data-testid="park-waiting-parts-btn"
                  >
                    Park → Waiting for Parts
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start border-rose-300 text-rose-800 hover:bg-rose-50"
                    disabled={parkingInFlight}
                    onClick={() => handleParkAndRetry('blocked')}
                    data-testid="park-blocked-btn"
                  >
                    Park → Sublet
                  </Button>
                  {/* SM-side shortcut — open the conflicting JC in its
                      detail modal so the SM can read context (notes,
                      pending parts ETA, etc.) before deciding how to
                      park it. Mechanics rarely need this, but SMs do
                      when they're juggling multiple techs. */}
                  <Button
                    variant="ghost"
                    className="justify-start text-blue-700 hover:bg-blue-50"
                    disabled={parkingInFlight}
                    onClick={() => {
                      const id = activeJcConflict?.active?.job_card_id;
                      if (id) {
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('id', id);
                          return next;
                        });
                        setActiveJcConflict(null);
                      }
                    }}
                    data-testid="open-active-jc-btn"
                  >
                    Open active JC instead
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="ghost"
                disabled={parkingInFlight}
                onClick={() => setActiveJcConflict(null)}
                data-testid="park-cancel-btn"
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {commsDialog}
        {/* iter141 + iter147 — Bulk SMS to Booking / Quote / No Show columns (Status view) */}
        <BulkBookingSmsModal
          open={bulkSmsOpen}
          onOpenChange={setBulkSmsOpen}
          bookingIds={selectedIdsFor[bulkSmsSource] || bookingSelectedIds}
          onSent={() => {
            (setSelectedIdsFor[bulkSmsSource] || setBookingSelectedIds)(new Set());
            fetchKanban();
          }}
        />
      </div>
    </Layout>
  );
}
